import { useEffect, useState } from 'react';
import type { FormIdentity } from '@capture/shared';
import {
  parseXML,
  parseJSON,
  evaluateXPath,
  evaluateJsonPath,
} from '@capture/shared';
import './IdentityEditor.css';

interface Props {
  identity: FormIdentity;
  sampleRaw: string | null;
  onChange: (next: FormIdentity) => void;
  lastClickedPath?: string | null;
}

type TestResult =
  | { kind: 'none' }
  | { kind: 'no-sample' }
  | { kind: 'invalid'; message: string }
  | { kind: 'match'; value: string }
  | { kind: 'no-match'; value: string | null };

/**
 * Editor for a single `FormIdentity` clause — the form-class selector that
 * routes incoming batches to a published job-type version. Runs a local
 * "test against sample" check (matching the same grammar + semantics as
 * the API's `resolveFormByHeader`) so the author sees immediate feedback.
 */
export function IdentityEditor({
  identity,
  sampleRaw,
  onChange,
  lastClickedPath,
}: Props) {
  const [result, setResult] = useState<TestResult>({ kind: 'none' });

  // Local input state so text fields work correctly when the parent
  // echoes props asynchronously (debounced PATCH, RQ re-render). Kept
  // in sync with props via useEffect; `onChange` is fired on every
  // keystroke so parents that DO echo props still see live updates.
  const initialPath =
    identity.format === 'xml' ? identity.xpath ?? '' : identity.path ?? '';
  const initialValueText = Array.isArray(identity.value)
    ? identity.value.join(',')
    : identity.value;
  const [pathDraft, setPathDraft] = useState(initialPath);
  const [valueDraft, setValueDraft] = useState(initialValueText);
  useEffect(() => setPathDraft(initialPath), [initialPath]);
  useEffect(() => setValueDraft(initialValueText), [initialValueText]);

  function runTest() {
    if (!sampleRaw) {
      setResult({ kind: 'no-sample' });
      return;
    }
    const parsed = safeParse(sampleRaw, identity.format);
    if (!parsed.ok) {
      setResult({ kind: 'invalid', message: parsed.error });
      return;
    }
    const extracted = extractValue(parsed, identity);
    if (extracted === null) {
      setResult({ kind: 'no-match', value: null });
      return;
    }
    const ok = matchesOp(extracted, identity);
    setResult(ok ? { kind: 'match', value: extracted } : { kind: 'no-match', value: extracted });
  }

  function handlePathChange(next: string) {
    setPathDraft(next);
    if (identity.format === 'xml') {
      onChange({ ...identity, xpath: next });
    } else {
      onChange({ ...identity, path: next });
    }
  }

  function handleValueChange(next: string) {
    setValueDraft(next);
    if (identity.op === 'in') {
      onChange({
        ...identity,
        value: next.split(',').map((s) => s.trim()).filter(Boolean),
      });
    } else {
      onChange({ ...identity, value: next });
    }
  }

  return (
    <div className="identity-editor">
      <header className="identity-editor__header">
        <h3 className="identity-editor__title">Form Identity (Classifier rule)</h3>
        <p className="identity-editor__subline">
          This rule decides which Job Type version handles incoming batches.
          It&apos;s not a header field — it&apos;s the routing key.
        </p>
      </header>
      <div className="identity-editor__row">
        <label className="identity-editor__label identity-editor__label--grow">
          <span>Path</span>
          <input
            className="identity-editor__input"
            value={pathDraft}
            onChange={(e) => handlePathChange(e.target.value)}
            placeholder={identity.format === 'xml' ? '/R/@class' : 'meta.kind'}
          />
        </label>
        <button
          type="button"
          className="identity-editor__last-clicked-btn"
          disabled={!lastClickedPath}
          onClick={() => {
            if (!lastClickedPath) return;
            if (identity.format === 'xml') {
              onChange({ ...identity, xpath: lastClickedPath });
            } else {
              onChange({ ...identity, path: lastClickedPath });
            }
            // onChange persists to the form-config store; setPathDraft updates the
            // local input draft so the user sees the change immediately (the draft is
            // echoed from props via useEffect on the next render anyway, but explicit
            // sync avoids a flicker).
            setPathDraft(lastClickedPath);
          }}
          title={
            lastClickedPath
              ? `Set Path to the most recently clicked tree node`
              : `Click a node in the sample tree first`
          }
        >
          ← Use last-clicked path
        </button>
      </div>
      <div className="identity-editor__row">
        <label className="identity-editor__label">
          <span>Op</span>
          <select
            className="identity-editor__select"
            value={identity.op}
            onChange={(e) =>
              onChange({
                ...identity,
                op: e.target.value as FormIdentity['op'],
              })
            }
          >
            <option value="eq">eq</option>
            <option value="in">in</option>
            <option value="matches">matches</option>
          </select>
        </label>
        <label className="identity-editor__label identity-editor__label--grow">
          <span>Value</span>
          <input
            className="identity-editor__input"
            value={valueDraft}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder={identity.op === 'in' ? 'INV,PO,CR' : 'INV'}
          />
        </label>
      </div>
      <div className="identity-editor__row">
        <button
          type="button"
          className="identity-editor__test-btn"
          onClick={runTest}
        >
          Test against sample
        </button>
        <TestResultChip result={result} />
      </div>
    </div>
  );
}

function TestResultChip({ result }: { result: TestResult }) {
  if (result.kind === 'none') return null;
  if (result.kind === 'no-sample') {
    return (
      <span className="identity-editor__chip identity-editor__chip--neutral">
        No sample attached
      </span>
    );
  }
  if (result.kind === 'invalid') {
    return (
      <span className="identity-editor__chip identity-editor__chip--danger" role="alert">
        Invalid sample: {result.message}
      </span>
    );
  }
  if (result.kind === 'match') {
    return (
      <span className="identity-editor__chip identity-editor__chip--success">
        match: {result.value}
      </span>
    );
  }
  return (
    <span className="identity-editor__chip identity-editor__chip--warn">
      no match
      {result.value !== null && (
        <span className="identity-editor__chip-detail">(got: {result.value})</span>
      )}
    </span>
  );
}

function safeParse(raw: string, format: 'xml' | 'json') {
  try {
    return format === 'xml' ? parseXML(raw) : parseJSON(raw);
  } catch (e) {
    return { ok: false as const, error: (e as Error).message };
  }
}

function extractValue(
  parsed:
    | Extract<ReturnType<typeof parseXML>, { ok: true }>
    | Extract<ReturnType<typeof parseJSON>, { ok: true }>,
  identity: FormIdentity,
): string | null {
  if (identity.format === 'xml') {
    if (!identity.xpath) return null;
    if (!('doc' in parsed)) return null;
    const r = evaluateXPath(parsed.doc, identity.xpath);
    return r.found ? r.value : null;
  }
  if (!identity.path) return null;
  if (!('root' in parsed)) return null;
  const r = evaluateJsonPath(parsed.root, identity.path);
  return r.found ? String(r.value) : null;
}

function matchesOp(extracted: string, identity: FormIdentity): boolean {
  if (identity.op === 'eq') return extracted === identity.value;
  if (identity.op === 'in') {
    return Array.isArray(identity.value) && identity.value.includes(extracted);
  }
  if (identity.op === 'matches') {
    try {
      return new RegExp(String(identity.value)).test(extracted);
    } catch {
      return false;
    }
  }
  return false;
}
